import { ref } from 'vue'
import { supportViewQuietErrors } from '@/utils/supportView'

export interface ToastAction {
    label: string
    handler: () => void
    /** Visual weight; defaults to primary. */
    variant?: 'primary' | 'secondary'
}

export type ToastPosition = 'left' | 'right'

export interface Toast {
    id: string
    type: 'success' | 'error' | 'info'
    message: string
    /** Single action (legacy). Prefer `actions` when multiple buttons are needed. */
    action?: ToastAction
    /** Multiple action buttons; takes precedence over `action` when non-empty. */
    actions?: ToastAction[]
    duration: number
    variant?: 'default' | 'progress' | 'loading'
    progress?: { current: number; total: number }
    progressNoun?: string
    /** Horizontal anchor; falls back to the surface default (usually right). */
    position?: ToastPosition
}

export interface ProgressToastHandle {
    id: string
    update: (current: number, total?: number) => void
    finish: (summary?: Omit<Toast, 'id' | 'duration'> & { duration?: number }) => void
    dismiss: () => void
}

const toasts = ref<Toast[]>([])
/** Extra px above the default toast anchor (bottom-4). Surfaces with fixed
 *  bottom chrome (e.g. mobile workbench footer) raise this so toasts don't
 *  cover primary actions. */
const toastInsetBottom = ref(0)
/** Horizontal default for new toasts. Surfaces like Inbox flip this to
 *  left so toasts clear the bottom-right compose / AI prompt area. */
const toastDefaultPosition = ref<ToastPosition>('right')
const MAX_VISIBLE = 3
/** Matches Tailwind `sm` / common app mobile breakpoint. */
const MOBILE_MEDIA_QUERY = '(max-width: 639px)'

let idCounter = 0

function isMobileViewport(): boolean {
    if (typeof window === 'undefined') return false
    return window.matchMedia(MOBILE_MEDIA_QUERY).matches
}

function resolvePosition(explicit?: ToastPosition): ToastPosition {
    return explicit ?? toastDefaultPosition.value
}

function showToast(toast: Omit<Toast, 'id' | 'duration'> & { duration?: number }) {
    // Support view: the backend just refused email content (403 `support_view_email_hidden`). The surface shows
    // a calm placeholder; a generic "couldn't load" toast from the same failure would only add noise.
    if (toast.type === 'error' && supportViewQuietErrors()) {
        return `toast-suppressed-${++idCounter}`
    }
    // Keep the mobile viewport focused on blocking failures and on toasts you can act on (Undo send) —
    // other success/info (and progress) toasts are desktop-only.
    if (toast.type !== 'error' && !toast.action && !toast.actions?.length && isMobileViewport()) {
        return `toast-suppressed-${++idCounter}`
    }

    const id = `toast-${++idCounter}-${Date.now()}`
    const entry: Toast = {
        ...toast,
        id,
        duration: toast.duration ?? (toast.type === 'error' ? 8000 : 4000),
        position: resolvePosition(toast.position),
    }

    toasts.value.push(entry)

    if (toasts.value.length > MAX_VISIBLE) {
        toasts.value.splice(0, toasts.value.length - MAX_VISIBLE)
    }

    if (entry.duration > 0) {
        setTimeout(() => dismissToast(id), entry.duration)
    }

    return id
}

function dismissToast(id: string) {
    const idx = toasts.value.findIndex(t => t.id === id)
    if (idx !== -1) {
        toasts.value.splice(idx, 1)
    }
}

function clearToasts() {
    toasts.value = []
}

function setToastInsetBottom(px: number) {
    toastInsetBottom.value = Math.max(0, px)
}

function setToastDefaultPosition(position: ToastPosition) {
    toastDefaultPosition.value = position
}

function showProgressToast(opts: {
    total: number
    message?: string
    progressNoun?: string
}): ProgressToastHandle {
    // Progress is info-typed; skip the live toast on mobile but still route the
    // finish summary through showToast so error summaries can surface.
    if (isMobileViewport()) {
        const id = `toast-suppressed-${++idCounter}`
        return {
            id,
            update: () => {},
            finish: (summary) => {
                if (summary) showToast(summary)
            },
            dismiss: () => {},
        }
    }

    const id = `toast-${++idCounter}-${Date.now()}`
    const entry: Toast = {
        id,
        type: 'info',
        variant: 'progress',
        message: opts.message ?? 'Sending drafts',
        progress: { current: 0, total: opts.total },
        progressNoun: opts.progressNoun,
        duration: 0,
        position: resolvePosition(),
    }
    toasts.value.push(entry)
    if (toasts.value.length > MAX_VISIBLE) {
        toasts.value.splice(0, toasts.value.length - MAX_VISIBLE)
    }

    return {
        id,
        update: (current: number, total?: number) => {
            const t = toasts.value.find(t => t.id === id)
            if (t && t.progress) {
                t.progress = {
                    current,
                    total: total ?? t.progress.total,
                }
            }
        },
        finish: (summary) => {
            dismissToast(id)
            if (summary) {
                showToast(summary)
            }
        },
        dismiss: () => dismissToast(id),
    }
}

export default function useToast() {
    return {
        toasts,
        toastInsetBottom,
        toastDefaultPosition,
        showToast,
        showProgressToast,
        dismissToast,
        clearToasts,
        setToastInsetBottom,
        setToastDefaultPosition,
    }
}
